import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import CorporationUtils from '@/common/utils/CorporationUtils';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import TranslationConstant from '@/common/constant/TranslationConstant';
import { tr } from '@/common/utils/tr';

/**
 * Helpers for auto-translation of fixed UI text (Loai 1 - AUTO).
 *
 * Language config is NOT stored in dedicated schema fields; it reuses:
 *   - Corporation.autoTranslation (Int)  -> org level ON/OFF
 *   - User.userSetting.mainLanguage      -> the user's main language
 *
 * Every unset/unknown value falls back to OFF / Japanese so tr() shows
 * the original text instead of blanking out.
 */

/**
 * Tag sysFields whose child option values are system-defined identifiers/enums
 * and must NOT be translated (RAW display type).
 * Used by DrilldownDisplayUtils and producer handlers (KnowledgeTicketHandler, etc.)
 */
export const NON_TRANSLATABLE_TAG_SYS_FIELDS = [
    // Phone/AI model fields - values are model IDs like "gpt-4o"
    Constant.AI_AUTO.SYSTEM_FIELD.CHATGPT_MODEL,
    Constant.AI_AUTO.SYSTEM_FIELD.REASONING_EFFORT,
    Constant.SYSTEM_FIELD.AI_TELEPHONE_REALTIME_MODEL,
    Constant.SYSTEM_FIELD.AI_TELEPHONE_APPLICABLE_PHONE_NUMBER,
    TelephoneConstant.TELEPHONE.SYSTEM_FIELD.CALL_HANDLER,
    // MCP server setting
    Constant.AI_AUTO.SYSTEM_FIELD.MCP_SERVER_SETTING,
    // TZL-001: 実行タイムゾーン - fixed English timezone labels, not translated
    Constant.SYSTEM_FIELD.EXECUTION_TIMEZONE,
    // AI Auto fields - values are system enum literals
];

/**
 * Normalizes a language code to the code expected by the dictionary and the MT
 * engine. Maps the legacy 'jp' of Constant.LANGUAGE_TYPE to 'ja'; any other code
 * is returned as-is (no case folding). Mirrors TranslateUtils.normalizeLangCode()
 * in the Lambda layer. Blank input falls back to the source language.
 */
export const normalizeLangCode = code => {
    if (typeof code !== 'string' || code.trim().length === 0) {
        return Constant.TRANSLATION_SOURCE_LANGUAGE;
    }

    const trimmed = code.trim();
    return trimmed === Constant.LANGUAGE_TYPE.JP ? Constant.TRANSLATION_LANGUAGE.JA : trimmed;
};

/**
 * Resolves a user's main language. A code that is not a key of
 * TRANSLATION_LANG_DISPLAY_JP ('EN', 'zh-tw', 'xx'...) is treated as the source
 * language, so translation is inactive and no API is called.
 * Mirrors TranslateUtils.resolveUserMainLanguage() in the Lambda layer.
 */
export const resolveMainLanguage = code => {
    const language = normalizeLangCode(code);
    return TranslationConstant.TRANSLATION_LANG_DISPLAY_JP[language] ? language : Constant.TRANSLATION_SOURCE_LANGUAGE;
};

/**
 * Reads the translation config out of the corporation + user detail atoms.
 * @returns {{enabled: boolean, mainLanguage: string, sourceLanguage: string}}
 */
export const resolveTranslationConfig = (corporationDetail, userDetail) => {
    const enabled = CorporationUtils.isEnableSettingMultiLanguage(corporationDetail);
    const userSetting = Utils.convertJSONDataToObject(userDetail?.userSetting) || {};

    return {
        enabled,
        mainLanguage: resolveMainLanguage(userSetting.mainLanguage),
        sourceLanguage: Constant.TRANSLATION_SOURCE_LANGUAGE
    };
};

/**
 * True when translated text should actually be fetched/displayed.
 * OFF, or a user whose main language IS the source language, needs no lookup.
 */
export const isTranslationActive = config =>
    Boolean(config?.enabled) && config.mainLanguage !== config.sourceLanguage;

/**
 * Number of characters as the user sees them (code points). String.length counts
 * UTF-16 units, so an emoji or a rare kanji like 𠮷 would count as 2.
 */
export const countTextChars = text => (typeof text === 'string' ? Array.from(text).length : 0);

/** True when a keyword / translation is longer than TRANSLATION_KEYWORD_MAX_LENGTH characters. */
export const isOverTranslationMaxLength = text => countTextChars(text) > Constant.TRANSLATION_KEYWORD_MAX_LENGTH;

/**
 * Fixed Japanese texts the backend joins with user/project names in display values
 * (e.g. 問い合わせ元, 投稿ユーザー, 更新ユーザー). Mirrors the backend constants.
 */
const FIXED_NAME_TEXTS = [
    Constant.PROJECT_GUEST_CUSTOMER_NAME,
    Constant.PROJECT_BASE_DISPLAY_NAME,
    Constant.PROJECT_BASE_NAME,
    Constant.DEFAULT_ANONYMOUS_NAME
];

const BRACKET_PAIRS = [
    ['(', ')'],
    ['（', '）']
];

/**
 * Translate only the fixed text inside a display name built by the backend.
 * User/project names are kept as-is.
 *
 *   'ゲストカスタマープロジェクト'            -> tr('ゲストカスタマープロジェクト')
 *   '山田 太郎 (組織内個人問い合わせ)'         -> '山田 太郎 (' + tr('組織内個人問い合わせ') + ')'
 *   '組織内個人問い合わせ (停止中)'            -> tr('組織内個人問い合わせ') + ' (停止中)'
 *   '山田 太郎 (未登録カスタマー)'             -> '山田 太郎 (' + tr('未登録カスタマー') + ')'
 *
 * @param {string} value display value from the API
 * @returns {string}
 */
export const translateFixedNameText = value => {
    if (typeof value !== 'string' || value.length === 0) {
        return value;
    }

    const trimmedValue = value.trim();
    if (FIXED_NAME_TEXTS.includes(trimmedValue)) {
        return tr(trimmedValue);
    }

    for (const fixedText of FIXED_NAME_TEXTS) {
        // Fixed text as a suffix in brackets: '<name> (<fixed text>)'
        for (const [open, close] of BRACKET_PAIRS) {
            const suffix = `${open}${fixedText}${close}`;
            if (trimmedValue.endsWith(suffix)) {
                return `${trimmedValue.slice(0, -suffix.length)}${open}${tr(fixedText)}${close}`;
            }
        }

        // Fixed text as a prefix followed by a bracket: '<fixed text> (<suffix>)'
        const prefix = `${fixedText} (`;
        if (trimmedValue.startsWith(prefix)) {
            return `${tr(fixedText)}${trimmedValue.slice(fixedText.length)}`;
        }
    }

    return value;
};

/**
 * Display name of a language in dropdowns: "{native} ({English})", e.g. "Español (Spanish)"
 * (half-width space before the bracket, requested by BA).
 * Languages whose native and English names are identical (English, Afrikaans...) show it once.
 * Unknown code -> the code itself (never throws).
 */
export const getLanguageDisplayName = code => {
    const nativeName = TranslationConstant.TRANSLATION_LANG_DISPLAY_JP[code];
    if (!nativeName) return code || '';
    const englishName = TranslationConstant.TRANSLATION_LANG_NAME_EN[code];
    return !englishName || englishName === nativeName ? nativeName : `${nativeName} (${englishName})`;
};

/** Same as getLanguageDisplayName plus the code, e.g. "Español (Spanish) (es)". */
export const getLanguageDisplayNameWithCode = code => `${getLanguageDisplayName(code)} (${code})`;

/**
 * Who set the main language stored in a User.userSetting (AWSJSON), same rule as the BE
 * TranslateUtils.resolveMainLanguageSetBy: a legacy value without a setter was written by the
 * profile screen (USER); no main language at all -> null (never set).
 */
export const resolveMainLanguageSetBy = userSetting => {
    const setting = Utils.convertJSONDataToObject(userSetting);
    if (!setting?.mainLanguage) return null;
    const setBy = setting.mainLanguageSetBy;
    return Object.values(TranslationConstant.MAIN_LANGUAGE_SET_BY).includes(setBy)
        ? setBy
        : TranslationConstant.MAIN_LANGUAGE_SET_BY.USER;
};

/** True when saving this edited row overwrites a main language the user set by themselves. */
export const isOverwritingUserMainLanguage = editedUser =>
    !!editedUser?.isChangeMainLanguage &&
    editedUser.originMainLanguageSetBy === TranslationConstant.MAIN_LANGUAGE_SET_BY.USER;

/**
 * 「いいえ」on the overwrite dialog (CR-2026-005 BR-9 / BR-9a): the main language of every row that
 * overwrites a value the user set goes back to that value. Every other row and every other field
 * (name, type, permission, main language not set by the user) is kept as edited.
 */
export const revertOverwrittenMainLanguage = editedList =>
    (editedList || []).map(row =>
        isOverwritingUserMainLanguage(row)
            ? { ...row, mainLanguage: row.originMainLanguage, isChangeMainLanguage: false }
            : row
    );

/** mainLanguage stored in a User.userSetting (AWSJSON); unset / unsupported -> default 'ja'. */
export const resolveMainLanguageFromUserSetting = userSetting => {
    const code = Utils.convertJSONDataToObject(userSetting)?.mainLanguage;
    return TranslationConstant.TRANSLATION_LANG_DISPLAY_JP[code] ? code : TranslationConstant.DEFAULT_MAIN_LANGUAGE;
};

const translationUtils = {
    normalizeLangCode,
    resolveMainLanguage,
    resolveTranslationConfig,
    isTranslationActive,
    countTextChars,
    isOverTranslationMaxLength,
    translateFixedNameText
};

export default translationUtils;
